import '../css/OnboardingFlow.less';

import gql from 'graphql-tag';
import React, {useEffect} from 'react';
import {useMutation, useQuery} from 'react-apollo';
import TextareaAutosize from 'react-textarea-autosize';
import {Button, Checkbox, Form, Input} from 'semantic-ui-react';
import classNames from 'classnames';
import config, {envIsLocal} from '../config';

import OnboardingBackground from '../assets/onboarding/signup-colorful-bkg.svg';
import CheckboxForm from '../components/CheckboxForm';
import MiniPagination from '../components/MiniPagination';
import RadioForm from '../components/RadioForm';
import WandbLoader from '../components/WandbLoader';
import {
  UPDATE_USER_MUTATION,
  UpdateUserMutationData,
  UpdateUserMutationVariables,
} from '../graphql/users';
import {UserInfo} from '../types/graphql';
import {slugFormat} from '../util/text';
import insertScript from '../util/insertScript';
import {doAuthRedirect} from '../util/auth';
import {useHistory, useLocation} from 'react-router-dom';
import makeComp from '../util/profiler';
import {toABTestKey, getABTestBool} from '../util/abTest';
import produce from 'immer';
import {Struct} from '../util/types';

const REQUIRED_ORG_NAME_AB_TEST_KEY = 'OnboardingFlow.requiredOrgName';
const requireOrgName = getABTestBool(REQUIRED_ORG_NAME_AB_TEST_KEY);
const track = getTrackWithABTrait(
  REQUIRED_ORG_NAME_AB_TEST_KEY,
  requireOrgName
);

const CHECK_AVAILABILITY_QUERY = gql`
  query CheckAvailability($name: String) {
    entity(name: $name) {
      id
      available
    }
  }
`;

interface CheckAvailabilityData {
  entity: {
    id: string;
    available: boolean;
  };
}

interface CheckAvailabilityVars {
  name: string;
}

const CREATE_USER_MUTATION = gql`
  mutation CreateEntityOnboardingFlow($name: String!) {
    createEntity(input: {name: $name}) {
      entity {
        id
      }
    }
  }
`;

interface CreateEntityOnboardingFlowData {
  entity: {
    id: string;
  };
}

interface CreateEntityOnboardingFlowVars {
  name: string;
}

const VIEWER_QUERY = gql`
  query ViewerInfo {
    viewer {
      id
      name
      userInfo
      entity
      username
    }
  }
`;

interface ViewerData {
  viewer: {
    id: string;
    name: string;
    userInfo?: UserInfo;
    entity: string;
    username?: string;
  };
}

interface OnboardingProps {
  auth?: any;
}

const OnboardingFlow: React.FC<OnboardingProps> = makeComp(
  ({auth}) => {
    const {
      loading: loadingAvailability,
      data: availabilityData,
      refetch: refetchAvailability,
    } = useQuery<CheckAvailabilityData, CheckAvailabilityVars>(
      CHECK_AVAILABILITY_QUERY
    );
    // Indicates if we're accepting an invite or resetting our password for W&B local
    const localFlow = auth && auth.tempKey && envIsLocal;
    const changeEmailFlow = auth && auth.claims && auth.claims.reset;
    let localUsername = (auth && auth.claims && auth.claims.name) || '';
    let isExistingUser = false;

    const {data: viewerData} = useQuery<ViewerData>(VIEWER_QUERY, {
      onCompleted: data => {
        if (!data.viewer) {
          // In wandb local, new users will not exist
          return;
        }
        if (
          data.viewer.username != null &&
          data.viewer.entity !== 'models' &&
          !localFlow
        ) {
          // Skip to the questions if the username is already set.
          // I can't just query for the username because it just
          // returns the user's name if their username isn't set.
          // But entity always returns the string 'models' until
          // the user creates their first entity, which sets their username
          // to the name of the entity. Because the world is a wild place.
          goToNextPage();
        } else {
          track(
            'Entered account creation',
            {userID: data.viewer.id},
            {integrations: {All: true, Salesforce: true}}
          );
        }
        if (
          data.viewer.name &&
          !data.viewer.name.includes('@') &&
          data.viewer.name.match(/^[A-Z]/)
        ) {
          // We made a mistake of setting email to name after a
          // user signs up via email+password.
          // This is a hack to ignore email-like names.
          setName(data.viewer.name);
        }
        const {userInfo} = data.viewer;
        if (userInfo) {
          if (userInfo.howOften) {
            setHowOften(userInfo.howOften);
          }
          if (userInfo.trainReasons) {
            setTrainReasons(userInfo.trainReasons);
          }
          if (userInfo.frameworks) {
            setFrameworks(userInfo.frameworks);
          }
          if (userInfo.broughtBy) {
            setBroughtBy(userInfo.broughtBy);
          }
          if (userInfo.teamSize) {
            setTeamSize(userInfo.teamSize);
          }
          if (userInfo.learningFrom) {
            setLearningFrom(userInfo.learningFrom);
          }
          if (userInfo.howOftenDetails) {
            setHowOftenDetails(userInfo.howOftenDetails);
          }
          if (userInfo.trainReasonsDetails) {
            setTrainReasonsDetails(userInfo.trainReasonsDetails);
          }
          if (userInfo.frameworksDetails) {
            setFrameworksDetails(userInfo.frameworksDetails);
          }
          if (userInfo.broughtByDetails) {
            setBroughtByDetails(userInfo.broughtByDetails);
          }
          if (userInfo.teamSizeDetails) {
            setTeamSizeDetails(userInfo.teamSizeDetails);
          }
          if (userInfo.learningFromDetails) {
            setLearningFromDetails(userInfo.learningFromDetails);
          }
        }
      },
    });

    // User already exists
    if (
      viewerData &&
      viewerData.viewer.username != null &&
      viewerData.viewer.entity !== 'models' &&
      viewerData.viewer.entity !== 'local'
    ) {
      localUsername = viewerData.viewer.entity;
      isExistingUser = true;
    }

    const history = useHistory();
    const location = useLocation();
    useEffect(() => {
      if (localFlow && isExistingUser) {
        console.error(
          'tried to load signup page for existing user: redirecting to password reset'
        );
        history.replace({
          ...location,
          pathname: 'change-password',
        });
      }
    }, [history, location, isExistingUser, localFlow]);

    const [updateUser] = useMutation<
      UpdateUserMutationData,
      UpdateUserMutationVariables
    >(UPDATE_USER_MUTATION);

    const [createEntityOnboardingFlow] = useMutation<
      CreateEntityOnboardingFlowData,
      CreateEntityOnboardingFlowVars
    >(CREATE_USER_MUTATION);

    const [name, setName] = React.useState('');
    const [orgName, setOrgName] = React.useState('');
    const [username, setUsername] = React.useState(localUsername);
    const [password, setPassword] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [acceptedTerms, setAcceptedTerms] = React.useState(false);
    const [pageHistory, setPageHistory] = React.useState(['start']);

    const [howOften, setHowOften] = React.useState('');
    const [howOftenDetails, setHowOftenDetails] = React.useState('');

    const [trainReasons, setTrainReasons] = React.useState<{
      [key: string]: boolean;
    }>({});
    const [trainReasonsDetails, setTrainReasonsDetails] = React.useState('');

    const [frameworks, setFrameworks] = React.useState<{
      [key: string]: boolean;
    }>({});
    const [frameworksDetails, setFrameworksDetails] = React.useState('');

    const [broughtBy, setBroughtBy] = React.useState<{
      [key: string]: boolean;
    }>({});
    const [broughtByDetails, setBroughtByDetails] = React.useState('');

    const [teamSize, setTeamSize] = React.useState('');
    const [teamSizeDetails, setTeamSizeDetails] = React.useState('');

    const [learningFrom, setLearningFrom] = React.useState<{
      [key: string]: boolean;
    }>({});
    const [learningFromDetails, setLearningFromDetails] = React.useState('');

    useEffect(() => {
      const currentPage = pageHistory[pageHistory.length - 1];
      track(`OnboardingFlow: started ${currentPage}`);
    }, [pageHistory]);

    // This is true when we're in a VM or onprem
    const isPrivateInstall = config.ENVIRONMENT_IS_PRIVATE;

    const goToNextPage = () => {
      setPageHistory(ph => {
        let nextPage: string | null = null;
        switch (ph[ph.length - 1]) {
          case 'start':
            if (isPrivateInstall) {
              // Only for VM currently
              finishSurvey();
            } else {
              nextPage = 'howOftenForm';
            }
            break;
          case 'howOftenForm':
            nextPage =
              howOften === 'never' ? 'broughtByForm' : 'trainReasonsForm';
            break;
          case 'trainReasonsForm':
            nextPage = 'frameworksForm';
            break;
          case 'frameworksForm':
            finishSurvey();
            break;
          case 'broughtByForm':
            nextPage =
              broughtBy.managing || broughtBy.team
                ? 'teamSizeForm'
                : 'learningFromForm';
            break;
          case 'teamSizeForm':
            finishSurvey();
            break;
          case 'learningFromForm':
            finishSurvey();
            break;
          default:
            throw new Error('Invalid page');
        }
        return nextPage ? [...ph, nextPage] : ph;
      });
    };

    const goToPreviousPage = () => {
      setPageHistory(ph => ph.slice(0, ph.length - 1));
    };

    if (!viewerData) {
      return <WandbLoader />;
    }

    const usernameIsValid = (uname: string) =>
      3 <= uname.length && uname.length <= 30;

    const usernameIsAvailable = (uname: string) =>
      usernameIsValid(uname) &&
      availabilityData &&
      (!availabilityData.entity || availabilityData.entity.available);

    const nameComplete = Boolean(name);
    const orgNameComplete = requireOrgName ? Boolean(orgName) : true;
    const startPageComplete =
      (envIsLocal ? true : nameComplete && orgNameComplete) && acceptedTerms;
    const createEntityDisabled =
      !startPageComplete ||
      !usernameIsAvailable(username) ||
      (changeEmailFlow && !email.includes('@'));

    const finishSurvey = () => {
      if ((window as any).analyticsOK) {
        try {
          // asynchronously record the conversion with google adwords
          insertScript(
            'https://www.googletagmanager.com/gtag/js?id=AW-715597483'
          ).then(() => {
            (window as any).dataLayer = (window as any).dataLayer || [];
            function gtag(...args: any[]) {
              (window as any).dataLayer.push(args);
            }
            gtag('js', new Date());
            gtag('config', 'AW-715597483');
            gtag('event', 'conversion', {
              send_to: 'AW-715597483/kaoICIyko7QBEKvNnNUC',
            });
          });
        } catch (e) {
          console.error('Error when attempting to register conversion event:');
          console.error(e);
        }
      }

      doAuthRedirect();
    };

    const makeAddDetailsField = (
      value: string,
      setValue: (newValue: string) => void
    ) => (
      <Form className="add-details-field">
        <Form.TextArea
          maxLength={512}
          rows="2"
          minRows={2}
          onChange={e => {
            setValue(e.currentTarget.value);
          }}
          placeholder="(Optional) Add some details"
          value={value}
          control={TextareaAutosize}
        />
      </Form>
    );

    const updateUserInfo = (newFields: Partial<UserInfo>) =>
      updateUser({
        variables: {
          userInfo: JSON.stringify(
            Object.assign({}, viewerData.viewer.userInfo, newFields)
          ),
        },
      });

    const pages: {[key: string]: React.ReactNode} = {
      start: (
        <>
          <h2>Create an account</h2>
          {changeEmailFlow && (
            <p style={{fontStyle: 'italic', marginTop: -13}}>
              Creating an account ensures your local instance is secure. You can
              add additional users from the Users page.
            </p>
          )}
          <Form>
            {!localFlow && (
              <>
                <Form.Field
                  className="small-margin"
                  data-test="name-input"
                  control={Input}
                  value={name}
                  onChange={(e: any) => setName(e.target.value)}
                  placeholder="Full name"
                  maxLength={64}
                />
                <Form.Field
                  data-test="org-input"
                  control={Input}
                  value={orgName}
                  onChange={(e: any) => setOrgName(e.target.value)}
                  onFocus={() => track('OnboardingFlow: focused org-input')}
                  placeholder="Organization"
                  maxLength={64}
                />
              </>
            )}
            {changeEmailFlow && (
              <Form.Field
                data-test="email-input"
                control={Input}
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                placeholder="Email address"
                maxLength={64}
              />
            )}
            <div>
              Choose your username carefully! It cannot be changed after signup.
            </div>
            <div className="username-prefix">wandb.ai/</div>
            <Form.Field
              className={classNames('username-field', {
                taken: username && !usernameIsAvailable(username),
              })}
              control={Input}
              data-test="username-input"
              loading={loadingAvailability}
              value={username}
              maxLength={30}
              onChange={(e: any) => {
                const newUsername = slugFormat(e.target.value);
                setUsername(newUsername);
                if (usernameIsValid(newUsername)) {
                  refetchAvailability({name: newUsername});
                }
              }}
              placeholder="Username"
              autoComplete="new-password"
            />
            <div
              className={classNames('taken-text', {
                active: username && !usernameIsAvailable(username),
              })}>
              This username is taken, try another
            </div>
            {localFlow && (
              <Form.Field
                control={Input}
                type="password"
                autoComplete="new-password"
                data-test="username-password"
                value={password}
                onChange={(e: any) => {
                  setPassword(e.target.value);
                }}
                placeholder="Password"
              />
            )}
            <Form.Field
              inline
              id="agree-to-terms-checkbox"
              control={Checkbox}
              onChange={() => setAcceptedTerms(!acceptedTerms)}
              checked={acceptedTerms}
              label={{
                children: (
                  <span>
                    <span data-test="agree-to-terms-checkbox">I</span> agree to
                    the{' '}
                    <a
                      href="/site/terms"
                      target="_blank"
                      rel="noopener noreferrer">
                      Terms and Conditions
                    </a>
                  </span>
                ),
              }}
            />
          </Form>
          <div className="actions">
            <Button
              className="next-button"
              data-test="create-account-button"
              primary
              disabled={createEntityDisabled}
              onClick={async () => {
                track('OnboardingFlow: completed start');
                if (localFlow) {
                  if (password.length < 5) {
                    return alert('Password of at least 5 characters required');
                  } else {
                    if (!(await auth.changePassword(password, email))) {
                      // Invalid token, auth handles messaging
                      return;
                    }
                  }
                  createEntityOnboardingFlow({variables: {name: username}})
                    .then(() => {
                      updateUser({variables: {defaultEntity: username}}).then(
                        finishSurvey
                      );
                    })
                    .catch(finishSurvey);
                } else {
                  await createEntityOnboardingFlow({
                    variables: {name: username},
                  });
                  // We need to explicitly enable Salesforce for this event
                  // to propagate through segment.
                  track(
                    'Signed Up',
                    {username},
                    {integrations: {All: true, Salesforce: true}}
                  );
                  const variables: UpdateUserMutationVariables = {name};
                  if (orgName) {
                    variables.userInfo = JSON.stringify({company: orgName});
                  }
                  await updateUser({variables});
                  goToNextPage();
                  track(
                    'Finished account creation',
                    {userID: viewerData.viewer.id, username, name, orgName},
                    {integrations: {All: true, Salesforce: true}}
                  );
                }
              }}>
              Continue
            </Button>
          </div>
        </>
      ),
      howOftenForm: (
        <>
          <h3>How often do you train models?</h3>
          <RadioForm
            name="howOftenGroup"
            options={[
              {label: 'Never', key: 'never'},
              {label: 'A few times a year', key: 'few-year'},
              {label: 'Every month', key: 'every-month'},
              {label: 'Every week', key: 'every-week'},
              {label: 'Every day', key: 'every-day'},
            ]}
            value={howOften}
            onChange={newValue => setHowOften(newValue)}
          />
          {makeAddDetailsField(howOftenDetails, setHowOftenDetails)}
          <div className="actions">
            <Button
              className="next-button"
              primary
              disabled={!howOften}
              onClick={() => {
                track('OnboardingFlow: completed howOftenForm');
                updateUserInfo({howOften, howOftenDetails}).then(goToNextPage);
              }}>
              Continue
            </Button>
          </div>
        </>
      ),
      trainReasonsForm: (
        <>
          <h3>Why do you train models?</h3>
          <CheckboxForm
            value={trainReasons}
            onChange={newValue => setTrainReasons(newValue)}
            options={[
              {label: 'Personal projects', key: 'personal'},
              {label: 'Open source projects', key: 'open-source'},
              {label: 'Academic research', key: 'academic'},
              {label: 'Work at a startup', key: 'startup'},
              {label: 'Work at a large company', key: 'large-company'},
            ]}
          />
          {makeAddDetailsField(trainReasonsDetails, setTrainReasonsDetails)}
          <div className="actions">
            <Button
              key="back"
              className="back-button"
              onClick={goToPreviousPage}>
              Back
            </Button>
            <Button
              className="next-button"
              primary
              disabled={Object.values(trainReasons).every(checked => !checked)}
              onClick={() => {
                track('OnboardingFlow: completed trainReasonsForm');
                updateUserInfo({trainReasons, trainReasonsDetails}).then(
                  goToNextPage
                );
              }}>
              Continue
            </Button>
          </div>
        </>
      ),
      frameworksForm: (
        <>
          <h3>What frameworks are you using right now?</h3>
          <div className="framework-options">
            <CheckboxForm
              value={frameworks}
              onChange={newValue => setFrameworks(newValue)}
              options={[
                {label: 'Keras', key: 'keras'},
                {label: 'PyTorch', key: 'pytorch'},
                {label: 'PyTorch Lightning', key: 'pytorch-lightning'},
                {label: 'TensorFlow', key: 'tensorflow'},
                {label: 'Scikit Learn', key: 'sklearn'},
                {label: 'Fast.ai', key: 'fastai'},
                {label: 'JAX', key: 'jax'},
                {label: 'SpaCy', key: 'spacy'},
                {label: 'XGBoost', key: 'xgboost'},
                {label: 'Julia Flux', key: 'julia-flux'},
                {label: 'MLLib', key: 'mllib'},
                {label: 'MXNet', key: 'mxnet'},
                {label: 'Other', key: 'other'},
              ]}
            />
          </div>
          {makeAddDetailsField(frameworksDetails, setFrameworksDetails)}
          <div className="actions">
            <Button
              key="back"
              className="back-button"
              onClick={goToPreviousPage}>
              Back
            </Button>
            <Button
              key="start"
              className="next-button"
              primary
              disabled={Object.values(frameworks).every(checked => !checked)}
              onClick={() => {
                track('OnboardingFlow: completed frameworksForm');
                updateUserInfo({frameworks, frameworksDetails}).then(
                  goToNextPage
                );
              }}>
              Get started
            </Button>
          </div>
        </>
      ),
      broughtByForm: (
        <>
          <h3>What brought you to W&amp;B?</h3>
          <CheckboxForm
            name="broughtByForm"
            value={broughtBy}
            onChange={newValue => setBroughtBy(newValue)}
            options={[
              {label: "I'm managing a team doing ML", key: 'managing'},
              {label: "I'm part of a team doing ML", key: 'team'},
              {label: "I'm curious about machine learning", key: 'curious'},
              {label: 'Other', key: 'other'},
            ]}
          />
          {makeAddDetailsField(broughtByDetails, setBroughtByDetails)}
          <div className="actions">
            <Button
              key="back"
              className="back-button"
              onClick={goToPreviousPage}>
              Back
            </Button>
            <Button
              className="next-button"
              primary
              disabled={Object.values(broughtBy).every(checked => !checked)}
              onClick={() => {
                track('OnboardingFlow: completed broughtByForm');
                updateUserInfo({broughtBy, broughtByDetails}).then(
                  goToNextPage
                );
              }}>
              Continue
            </Button>
          </div>
        </>
      ),
      teamSizeForm: (
        <>
          <h3>How many people are training models on your team?</h3>
          <RadioForm
            name="teamSizeGroup"
            value={teamSize}
            onChange={newValue => setTeamSize(newValue)}
            options={[
              {label: '1 - 5', key: '1-5'},
              {label: '5 - 10', key: '5-10'},
              {label: '10 - 30', key: '10-30'},
              {label: '30+', key: '30+'},
            ]}
          />
          {makeAddDetailsField(teamSizeDetails, setTeamSizeDetails)}
          <div className="actions">
            <Button className="back-button" onClick={goToPreviousPage}>
              Back
            </Button>
            <Button
              key="start"
              className="next-button"
              primary
              disabled={!teamSize}
              onClick={() => {
                track('OnboardingFlow: completed teamSizeForm');
                updateUserInfo({teamSize, teamSizeDetails}).then(goToNextPage);
              }}>
              Get started
            </Button>
          </div>
        </>
      ),
      learningFromForm: (
        <>
          <h3>Where are you learning about ML?</h3>
          <CheckboxForm
            name="learningFromForm"
            value={learningFrom}
            onChange={newValue => setLearningFrom(newValue)}
            options={[
              {label: 'University', key: 'university'},
              {label: 'Online course', key: 'online'},
              {label: 'Self-directed study', key: 'self'},
              {label: 'Other', key: 'other'},
            ]}
          />
          {makeAddDetailsField(learningFromDetails, setLearningFromDetails)}
          <div className="actions">
            <Button className="back-button" onClick={goToPreviousPage}>
              Back
            </Button>
            <Button
              key="start"
              className="next-button"
              primary
              disabled={Object.values(learningFrom).every(checked => !checked)}
              onClick={() => {
                track('OnboardingFlow: completed learningFromForm');
                updateUserInfo({learningFrom, learningFromDetails}).then(
                  goToNextPage
                );
              }}>
              Get started
            </Button>
          </div>
        </>
      ),
    };

    return (
      <div
        className="onboarding-flow"
        style={{backgroundImage: `url(${OnboardingBackground})`}}>
        <div className="onboarding-dialogue">
          {pageHistory.length > 1 && (
            <MiniPagination
              pageCount={3}
              currentPage={pageHistory.length - 2}
            />
          )}
          {pages[pageHistory[pageHistory.length - 1]]}
        </div>
      </div>
    );
  },
  {id: 'OnboardingFlow'}
);

export default OnboardingFlow;

function getTrackWithABTrait(key: string, val: any) {
  function trackWithTraits(
    event: string,
    properties: Struct = {},
    opts: Struct = {}
  ): void {
    const newOpts = produce(opts, draft => {
      draft.context = draft.context ?? {};
      draft.context.traits = {
        [toABTestKey(key)]: val,
        ...(draft.context.traits ?? {}),
      };
    });
    window.analytics.track(event, properties, newOpts);
  }
  return trackWithTraits;
}
